import {Injectable, OnDestroy} from '@angular/core';
import {SyncStatusItemsTable} from '../../tables/sync-status-items.table';
import {SyncStatusItem} from '../../interfaces/core/sync-status-item';
import {TableSyncSettings} from '../../interfaces/core/table-sync-settings';
import {CdRaisTable} from '../../tables/care/cd-rais.table';
import {CdMdsAnswersTable} from '../../tables/care/cd-mds-answers.table';
import {WmWoundDocumentationEntriesTable} from '../../tables/wound-management/wm-wound-documentation-entries.table';
import {SyncItem} from '../../interfaces/core/sync-item';
import {WmWoundAnswersTable} from '../../tables/wound-management/wm-wound-answers.table';
import {db} from '../../tables/db.service';
import {WmWoundImagesTable} from '../../tables/wound-management/wm-wound-images.table';
import {WmWoundDocumentationsTable} from '../../tables/wound-management/wm-wound-documentations.table';
import {environment} from '../../../environments/environment';
import {CdAimEvaluationsTable} from '../../tables/care/cd-aim-evaluations.table';
import {AuthService} from './auth.service';
import {SyncException} from '../../exceptions/sync.exception';
import {Subscription} from 'rxjs';
import {ConnectionService} from './connection.service';
import {ConflictHandler} from '../../tables/conflict-handling/conflict-handler';
import {ErrorLogsTable} from '../../tables/error-logs/error-logs.table';
import {TtTravelEntriesTable} from '../../tables/time-tracking/tt-travel-entries.table';
import {TtActivityEntriesTable} from '../../tables/time-tracking/tt-activity-entries.table';
import {TtProductEntriesTable} from '../../tables/time-tracking/tt-product-entries.table';
import {CustomerDocumentsTable} from '../../tables/customer-documents/customer-documents.table';
import {ProdRequestsTable} from '../../tables/product-management/prod-requests.table';
import {HttpService} from './http.service';
import {VitalSignEntriesTable} from '../../tables/vital-signs/vital-sign-entries.table';
import {PrProcessReportsTable} from '../../tables/process-reports/pr-process-reports.table';
import {TtLiveActivityEntriesTable} from '../../tables/time-tracking/tt-live-activity-entries.table';
import {CdExecutionProofsTable} from '../../tables/care/cd-execution-proofs.table';
import {CdTemporaryMeasuresTable} from '../../tables/care/cd-temporary-measures.table';

@Injectable({
  providedIn: 'root'
})
export class SyncService implements OnDestroy {

  private _tableSyncSettings: {
    [key: string]: TableSyncSettings
  };

  private _syncStatusItems: SyncStatusItem[] = [];
  private _syncing: boolean = false;
  private _isSynced: boolean = false;
  private _interval: any = null;
  private _isConnected: boolean = false;
  private _lockSyncing: boolean = false;

  private $_isConnected: Subscription;
  private $_syncStatusItemsChanges: Subscription;

  constructor(private syncStatusItemsTable: SyncStatusItemsTable,
              private connectionService: ConnectionService,
              private cdRaisTable: CdRaisTable,
              private httpService: HttpService,
              private cdAimEvaluationsTable: CdAimEvaluationsTable,
              private cdMdsAnswersTable: CdMdsAnswersTable,
              private conflictHandler: ConflictHandler,
              private errorLogsTable: ErrorLogsTable,
              private vitalSignEntriesTable: VitalSignEntriesTable,
              private wmWoundImagesTable: WmWoundImagesTable,
              private wmWoundDocumentationsTable: WmWoundDocumentationsTable,
              private wmWoundDocumentationEntriesTable: WmWoundDocumentationEntriesTable,
              private wmWoundAnswersTable: WmWoundAnswersTable,
              private ttActivityEntriesTable: TtActivityEntriesTable,
              private ttProductEntriesTable: TtProductEntriesTable,
              private customerDocumentsTable: CustomerDocumentsTable,
              private ttTravelEntriesTable: TtTravelEntriesTable,
              private processReportsTable: PrProcessReportsTable,
              private ttLiveActivityEntriesTable: TtLiveActivityEntriesTable,
              private cdExecutionProofsTable: CdExecutionProofsTable,
              private cdTemporaryMeasuresTable: CdTemporaryMeasuresTable,
              private prodRequestsTable: ProdRequestsTable) {
  }

  get syncing(): boolean {
    return this._syncing;
  }

  get isSynced(): boolean {
    return this._isSynced;
  }

  /**
   *
   */
  public ngOnDestroy(): void {
    if(this._interval) {
      clearInterval(this._interval);
    }
    this.$_syncStatusItemsChanges?.unsubscribe();
    this.$_isConnected?.unsubscribe();
  }

  /**
   * This function checks directly in the database if the sync is done
   */
  public async isSyncedSecurely(): Promise<boolean> {
    this._isSynced = await this.syncStatusItemsTable.isSynced();
    return this._isSynced;
  }

  /**
   *
   */
  public lockSyncing(): void {
    this._lockSyncing = true;
  }

  /**
   *
   */
  public unlockSyncing(forceSync: boolean = false): void {
    this._lockSyncing = false;
    this.processSyncing(forceSync).finally();
  }

  /**
   *
   */
  public async init(): Promise<void> {
    await this.ttLiveActivityEntriesTable.removeDuplicates();

    this._tableSyncSettings = {
      cdRais: this.cdRaisTable.tableSyncSettings,
      cdMdsAnswers: this.cdMdsAnswersTable.tableSyncSettings,
      cdAimEvaluations: this.cdAimEvaluationsTable.tableSyncSettings,
      wmWoundDocumentationEntries: this.wmWoundDocumentationEntriesTable.tableSyncSettings,
      wmWoundAnswers: this.wmWoundAnswersTable.tableSyncSettings,
      wmWoundImages: this.wmWoundImagesTable.tableSyncSettings,
      wmWoundDocumentations: this.wmWoundDocumentationsTable.tableSyncSettings,
      errorLogs: this.errorLogsTable.tableSyncSettings,
      ttTravelEntries: this.ttTravelEntriesTable.tableSyncSettings,
      ttActivityEntries: this.ttActivityEntriesTable.tableSyncSettings,
      ttProductEntries: this.ttProductEntriesTable.tableSyncSettings,
      customerDocuments: this.customerDocumentsTable.tableSyncSettings,
      prodRequests: this.prodRequestsTable.tableSyncSettings,
      vitalSignEntries: this.vitalSignEntriesTable.tableSyncSettings,
      ttLiveActivityEntries: this.ttLiveActivityEntriesTable.tableSyncSettings,
      cdExecutionProofs: this.cdExecutionProofsTable.tableSyncSettings,
      prProcessReports: this.processReportsTable.tableSyncSettings,
      cdTemporaryMeasures: this.cdTemporaryMeasuresTable.tableSyncSettings
    };

    this._isSynced = await this.syncStatusItemsTable.isSynced();

    // Waiting until init is finished | login trigger clear many times
    setTimeout(() => {
      this.initSyncInterval();

      this.$_isConnected = this.connectionService.isConnected$.subscribe((_isConnected) => {
        this._isConnected = _isConnected;
      });
    }, 500);
  }

  /**
   *
   * @private
   */
  private initSyncInterval(): void {
    if(this._interval) {
      clearInterval(this._interval);
    }
    this._interval = setInterval(() => {
      this.processSyncing().finally();
    }, environment.syncInterval);
  }

  /**
   *
   * @param force
   * @private
   */
  public async processSyncing(force: boolean = false): Promise<void> {
    try {
      // Zu synchronisierende Items holen
      this._syncStatusItems = await this.syncStatusItemsTable.getToSync();
      this._isSynced = this._syncStatusItems.length === 0;
    }
    catch(e) {
      console.error('Storage error:', e);
      this._isSynced = false;
      return;
    }

    // Falls manuell forciert wurde, Sync-Status zurücksetzen
    if (force) {
      this.httpService.cancelRequests();
      this.initSyncInterval();
      this._syncing = false;
    }

    // Vorzeitiger Abbruch, wenn gerade gesynct oder blockiert
    if(this._syncing || this._lockSyncing) {
      return;
    }

    // Authentifizierung prüfen
    if (!AuthService.isAuthenticated()) {
      this.infoLog('Synchronization: Not authenticated');
      return;
    }

    // Verbindung prüfen
    if (!this._isConnected) {
      this.infoLog('Synchronization: No connection');
      return;
    }

    // Jetzt ist klar: Wir dürfen syncen → Status setzen
    this._syncing = true;

    try {
      if(this._isSynced) {
        this.infoLog('Nothing to sync.');
        return;
      }
      else {
        this.infoLog('Synchronization started...');
      }

      while(this._syncStatusItems.length > 0) {
        await this.processSyncStatusItem(this._syncStatusItems[0]);
        await this.sleep(200);
        this._syncStatusItems = await this.syncStatusItemsTable.getToSync();
      }

      this.infoLog('Synchronization completed.');
      await this.sleep(2000);
    }
    catch (syncException) {
      this._isSynced = false;

      if (syncException instanceof SyncException) {
        console.error('[SyncService] SyncException:', syncException.message, syncException.code);
      }
      else if (!environment.production) {
        console.error('[SyncService] Unexpected sync error:', syncException);
      }

      this.infoLog('Synchronization failed.');
      await this.sleep(1000 * 10);
    }
    finally {
      this._syncing = false;
    }
  }

  /**
   *
   * @param syncStatusItem
   * @private
   */
  private async processSyncStatusItem(syncStatusItem: SyncStatusItem): Promise<void> {
    const tableSyncSetting = this._tableSyncSettings[syncStatusItem.tableName];
    if(!tableSyncSetting) {
      throw new SyncException('No sync settings for table: ' + syncStatusItem.tableName);
    }

    try {
      if(tableSyncSetting.syncMode === 'many') {
        await this.handleSyncMany(tableSyncSetting, syncStatusItem);
      }
      else if(tableSyncSetting.syncMode === 'all') {
        await this.handleSyncAll(tableSyncSetting, syncStatusItem);
      }
      else if(tableSyncSetting.syncMode === 'default') {
        if(syncStatusItem.deleted === 1) {
          await this.handleDeletion(tableSyncSetting, syncStatusItem);
        }
        else {
          await this.handleSync(tableSyncSetting, syncStatusItem);
        }
      }
    } catch(e: any) {
      throw e;
    }
  }

  /**
   *
   * @param tableSyncSetting
   * @param syncStatusItem
   * @private
   */
  private async handleDeletion(tableSyncSetting: TableSyncSettings, syncStatusItem: SyncStatusItem): Promise<void> {
    try {
      if(syncStatusItem.deleted === 1) {
        await tableSyncSetting.adapter.delete(syncStatusItem);
        await this.syncStatusItemsTable.setSyncDeletedItems([syncStatusItem]);
      }
    } catch(syncException) {
      if(syncException instanceof SyncException) {
        await this.syncStatusItemsTable.setSyncError([syncStatusItem.id], syncException);
      }
      throw syncException;
    }
  }

  /**
   *
   * @param tableSyncSetting
   * @param syncStatusItem
   * @private
   */
  private async handleSync(tableSyncSetting: TableSyncSettings, syncStatusItem: SyncStatusItem): Promise<void> {
    const table = db.getSyncTableByTableName(syncStatusItem.tableName);
    const itemToSync = await table.where('uuid').equals(syncStatusItem.primaryKey).first();

    if(!itemToSync) {
      throw new SyncException('Dependent item not found', 394);
    }

    try {
      await this.syncStatusItemsTable.setSyncInProcess([syncStatusItem.id]);
      await tableSyncSetting.adapter.save(itemToSync);
      await this.syncStatusItemsTable.setSyncSuccessfully([itemToSync], syncStatusItem.tableName);
    } catch(syncException) {
      if(syncException instanceof SyncException) {
        await this.syncStatusItemsTable.setSyncError([syncStatusItem.id], syncException);
        await this.conflictHandler.handleConflict(syncStatusItem, syncException);
      }
      throw syncException;
    }
  }

  /**
   *
   * @param tableSyncSetting
   * @param syncStatusItem
   * @private
   */
  private async handleSyncAll(tableSyncSetting: TableSyncSettings, syncStatusItem: SyncStatusItem): Promise<void> {
    const table = db.getSyncTableByTableName(syncStatusItem.tableName);
    const itemToSync = await table.where('uuid').equals(syncStatusItem.primaryKey).first();
    const foreignKey = tableSyncSetting.adapter.foreignKey;
    let itemsToSync: SyncItem[];

    if(foreignKey) {
      const foreignKeyValue = itemToSync[foreignKey] as any;
      itemsToSync = await table.where(foreignKey).equals(foreignKeyValue).toArray();
    }
    else {
      itemsToSync = await table.toArray();
    }

    if(itemsToSync.length <= 0) {
      throw new Error('Dependent items not found');
    }

    const syncStatusItems =
      await this.syncStatusItemsTable.getAllByPrimaryKey(itemsToSync.map(itemToSync => itemToSync.uuid));
    const syncStatusItemsIds = syncStatusItems.map(_syncStatusItem => _syncStatusItem.id);

    try {
      await this.syncStatusItemsTable.setSyncInProcess(syncStatusItemsIds);
      await tableSyncSetting.adapter.saveMany(itemsToSync);
      await this.syncStatusItemsTable.setSyncSuccessfully(itemsToSync, syncStatusItem.tableName);
    } catch(syncException) {
      if(syncException instanceof SyncException) {
        await this.syncStatusItemsTable.setSyncError(syncStatusItemsIds, syncException);
      }
      throw syncException;
    }
  }

  /**
   * TODO: Deletion is not handled yet
   *
   * @param tableSyncSetting
   * @param syncStatusItem
   * @private
   */
  private async handleSyncMany(tableSyncSetting: TableSyncSettings, syncStatusItem: SyncStatusItem): Promise<void> {
    await this.sleep(1000);

    const table = db.getSyncTableByTableName(syncStatusItem.tableName);
    const syncStatusItems: SyncStatusItem[] = [];
    for(const _syncStatusItem of this._syncStatusItems) {
      if(_syncStatusItem.tableName === syncStatusItem.tableName) {
        syncStatusItems.push(_syncStatusItem);
      }
      else {
        break;
      }
    }

    let itemsToSync =
      await table.where('uuid').anyOf(
        syncStatusItems.map(_syncStatusItem => _syncStatusItem.primaryKey)
      ).toArray();

    if(itemsToSync.length <= 0) {
      throw new Error('Dependent items not found');
    }

    const foreignKey = tableSyncSetting.adapter.foreignKey;
    if(itemsToSync.length > 0 && foreignKey) {
      const firstItemToSync = itemsToSync[0];
      const foreignKeyValue = firstItemToSync[foreignKey];
      itemsToSync = itemsToSync.filter(_itemToSync => _itemToSync[foreignKey] === foreignKeyValue);
    }

    try {
      await tableSyncSetting.adapter.saveMany(itemsToSync);
      await this.syncStatusItemsTable.setSyncSuccessfully(itemsToSync, syncStatusItem.tableName);
    } catch(syncException) {
      if(syncException instanceof SyncException) {
        const syncStatusItems =
          await this.syncStatusItemsTable.getAllByPrimaryKey(itemsToSync.map(itemToSync => itemToSync.uuid));
        await this.syncStatusItemsTable.setSyncError(syncStatusItems.map(syncStatusItem => syncStatusItem.id), syncException);
        await this.conflictHandler.handleConflict(syncStatusItem, syncException);
      }
      throw syncException;
    }
  }

  /**
   *
   * @param milliseconds
   * @private
   */
  private async sleep(milliseconds: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        resolve();
      }, milliseconds);
    });
  }

  /**
   *
   * @param info
   * @private
   */
  private infoLog(info: string): void {
    if(!environment.production) {
      console.log(info);
    }
  }

}
